import { useMemo } from "react";
import {
  ExternalLink,
  Loader2,
  MessageSquareQuote,
  Plus,
  RefreshCw,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { IngestReviewDialog } from "@/components/externalReview/IngestReviewDialog";
import { SentimentDialog } from "@/components/externalReview/SentimentDialog";
import { SourceCombobox } from "@/components/review/SourceCombobox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { JsonBlock } from "@/components/ui/json-block";
import { Pagination } from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  useExternalReviewsCount,
  useExternalReviewsList,
} from "@/hooks/useExternalReviews";
import { useDebounce } from "@/hooks/useDebounce";
import { useSessionState } from "@/hooks/useSessionState";
import { useSourcesList } from "@/hooks/useSources";
import { useUrlState } from "@/hooks/useUrlState";
import type { UrlStatePatch } from "@/hooks/useUrlState";
import type { ExternalReviewCountParams } from "@/services/externalReviewApi";
import type {
  ExternalReviewAdmin,
  ExternalReviewListParams,
  ExternalReviewPlatform,
} from "@/types/externalReview";
import {
  EXTERNAL_REVIEW_PLATFORMS,
  PLATFORM_LABELS,
} from "@/types/externalReview";
import type {
  ExternalReviewsSessionState,
  ExternalReviewsUrlState,
} from "@/types/pageState";

const ALL_VALUE = "__all";
const LIMIT_OPTIONS = [25, 50, 100, 200] as const;

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function truncateText(text: string | null, max = 80): string {
  if (!text?.trim()) return "—";
  const trimmed = text.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max)}…`;
}

function topEntries(
  record: Record<string, number>,
  limit = 4,
): [string, number][] {
  return Object.entries(record)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit);
}

export default function ExternalReviews() {
  const [urlState, setUrlState] = useUrlState<ExternalReviewsUrlState>({
    schema: {
      platform: "string",
      source_id: "string",
      has_text: "string",
      sentiment: "string",
      q: "string",
      url: "string",
      limit: "number",
      offset: "number",
    },
    defaults: {
      has_text: "any",
      sentiment: "needs_attention",
      limit: 50,
      offset: 0,
    },
    replace: true,
  });

  const [sessionState, setSessionState] =
    useSessionState<ExternalReviewsSessionState>({
      key: "external-reviews-ui",
      defaultValue: {
        sentimentDialogOpen: false,
        selectedReviewId: null,
        ingestDialogOpen: false,
      },
    });

  const limit = urlState.limit ?? 50;
  const offset = urlState.offset ?? 0;
  const sentimentFilter = urlState.sentiment ?? "needs_attention";
  const currentPage = Math.floor(offset / limit) + 1;
  const textQuery = useDebounce(urlState.q ?? "", 300).trim();
  const urlQuery = useDebounce(urlState.url ?? "", 300).trim();

  const listParams = useMemo((): ExternalReviewListParams => {
    const params: ExternalReviewListParams = {
      limit,
      offset,
    };
    if (sentimentFilter === "needs_attention") {
      params.has_sentiment = false;
      params.sort = "scraped_at_asc";
    } else {
      if (sentimentFilter === "scored") params.has_sentiment = true;
      params.sort = "scraped_at_desc";
    }
    if (urlState.source_id) params.source_id = urlState.source_id;
    if (urlState.platform && urlState.platform !== ALL_VALUE) {
      params.platform = urlState.platform as ExternalReviewPlatform;
    }
    if (urlState.has_text === "true") params.has_text = true;
    if (urlState.has_text === "false") params.has_text = false;
    if (textQuery) params.q = textQuery;
    if (urlQuery) params.url = urlQuery;
    return params;
  }, [
    limit,
    offset,
    sentimentFilter,
    textQuery,
    urlQuery,
    urlState.has_text,
    urlState.platform,
    urlState.source_id,
  ]);

  const countParams = useMemo(() => {
    const params: ExternalReviewCountParams = {};
    if (listParams.source_id) params.source_id = listParams.source_id;
    if (listParams.platform) params.platform = listParams.platform;
    if (listParams.has_text != null) params.has_text = listParams.has_text;
    if (listParams.has_sentiment != null) {
      params.has_sentiment = listParams.has_sentiment;
    }
    if (listParams.q) params.q = listParams.q;
    if (listParams.url) params.url = listParams.url;
    return params;
  }, [
    listParams.has_sentiment,
    listParams.has_text,
    listParams.platform,
    listParams.q,
    listParams.source_id,
    listParams.url,
  ]);

  const {
    data: reviews = [],
    isLoading: reviewsLoading,
    isFetching: reviewsFetching,
    refetch: refetchReviews,
  } = useExternalReviewsList(listParams);

  const {
    data: counts,
    isLoading: countsLoading,
    refetch: refetchCounts,
  } = useExternalReviewsCount(countParams);

  const sourcesQuery = useSourcesList(true);
  const sourceIds = useMemo(
    () => (sourcesQuery.data?.sources ?? []).map((s) => s.id),
    [sourcesQuery.data],
  );

  const selectedReview = useMemo(
    () =>
      reviews.find((r) => r.id === sessionState.selectedReviewId) ??
      (null as ExternalReviewAdmin | null),
    [reviews, sessionState.selectedReviewId],
  );

  const platformTiles = useMemo(
    () => topEntries(counts?.by_platform ?? {}),
    [counts?.by_platform],
  );
  const sourceTiles = useMemo(
    () => topEntries(counts?.by_source ?? {}),
    [counts?.by_source],
  );

  const resetOffset = (patch: UrlStatePatch<ExternalReviewsUrlState>) => {
    setUrlState((prev) => ({ ...prev, ...patch, offset: 0 }));
  };

  const handleRefresh = () => {
    void Promise.all([refetchReviews(), refetchCounts()]).then(() => {
      toast.success("Reviews refreshed");
    });
  };

  const openSentiment = (review: ExternalReviewAdmin) => {
    setSessionState((prev) => ({
      ...prev,
      sentimentDialogOpen: true,
      selectedReviewId: review.id,
    }));
  };

  return (
    <div className="space-y-6">
      <Toaster position="top-right" theme="dark" />

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">External Reviews</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Browse ingested external reviews and label sentiment for those that
            need attention.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={reviewsFetching}
          >
            <RefreshCw
              className={`w-4 h-4 ${reviewsFetching ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={() =>
              setSessionState((prev) => ({ ...prev, ingestDialogOpen: true }))
            }
          >
            <Plus className="w-4 h-4" />
            Add external review
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquareQuote className="w-5 h-5" />
            Overview
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Reviews matching the current filters.
          </p>
        </CardHeader>
        <CardContent>
          {countsLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading counts…
            </div>
          ) : counts ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="rounded-lg border p-4">
                <p className="text-sm text-muted-foreground">Total matching</p>
                <p className="text-2xl font-semibold">{counts.total}</p>
              </div>
              <div className="rounded-lg border p-4 space-y-2">
                <p className="text-sm text-muted-foreground">Top platforms</p>
                {platformTiles.length === 0 ? (
                  <p className="text-sm text-muted-foreground">—</p>
                ) : (
                  <ul className="space-y-1 text-sm">
                    {platformTiles.map(([platform, n]) => (
                      <li
                        key={platform}
                        className="flex items-center justify-between gap-2"
                      >
                        <span>
                          {PLATFORM_LABELS[
                            platform as ExternalReviewPlatform
                          ] ?? platform}
                        </span>
                        <Badge variant="secondary">{n}</Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="rounded-lg border p-4 space-y-2">
                <p className="text-sm text-muted-foreground">Top sources</p>
                {sourceTiles.length === 0 ? (
                  <p className="text-sm text-muted-foreground">—</p>
                ) : (
                  <ul className="space-y-1 text-sm">
                    {sourceTiles.map(([sourceId, n]) => (
                      <li
                        key={sourceId}
                        className="flex items-center justify-between gap-2"
                      >
                        <span className="font-mono text-xs truncate">
                          {sourceId}
                        </span>
                        <Badge variant="secondary">{n}</Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">
                Search text
              </label>
              <Input
                value={urlState.q ?? ""}
                onChange={(e) =>
                  resetOffset({ q: e.target.value || undefined })
                }
                placeholder="Match review text or author handle…"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">
                Link / thread URL
              </label>
              <Input
                value={urlState.url ?? ""}
                onChange={(e) =>
                  resetOffset({ url: e.target.value || undefined })
                }
                placeholder="Paste a thread permalink to see if it was ingested…"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">
                Sentiment
              </label>
              <Select
                value={sentimentFilter}
                onValueChange={(value) => resetOffset({ sentiment: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="needs_attention">
                    Needs attention
                  </SelectItem>
                  <SelectItem value="scored">Scored</SelectItem>
                  <SelectItem value="any">Any</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">
                Platform
              </label>
              <Select
                value={urlState.platform ?? ALL_VALUE}
                onValueChange={(value) =>
                  resetOffset({
                    platform: value === ALL_VALUE ? undefined : value,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All platforms" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_VALUE}>All platforms</SelectItem>
                  {EXTERNAL_REVIEW_PLATFORMS.map((p) => (
                    <SelectItem key={p} value={p}>
                      {PLATFORM_LABELS[p]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">
                Source
              </label>
              <SourceCombobox
                sources={sourceIds}
                isLoading={sourcesQuery.isLoading}
                value={urlState.source_id}
                onValueChange={(source) => resetOffset({ source_id: source })}
                className="w-full"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">
                Has text
              </label>
              <Select
                value={urlState.has_text ?? "any"}
                onValueChange={(value) => resetOffset({ has_text: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="true">With text</SelectItem>
                  <SelectItem value="false">No text</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">
                Page size
              </label>
              <Select
                value={String(limit)}
                onValueChange={(value) => resetOffset({ limit: Number(value) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LIMIT_OPTIONS.map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Reviews
            {counts ? (
              <Badge variant="outline">{counts.total} matching</Badge>
            ) : null}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {reviewsLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-8">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading reviews…
            </div>
          ) : reviews.length === 0 ? (
            <p className="text-muted-foreground py-8">
              No reviews match the current filters.
            </p>
          ) : (
            <TooltipProvider>
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Platform</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Author</TableHead>
                      <TableHead className="min-w-[200px]">Text</TableHead>
                      <TableHead>Rating</TableHead>
                      <TableHead>Sentiment</TableHead>
                      <TableHead>Posted</TableHead>
                      <TableHead>Scraped</TableHead>
                      <TableHead>Link</TableHead>
                      <TableHead>Raw</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reviews.map((review) => (
                      <TableRow key={review.id}>
                        <TableCell>
                          <Badge variant="secondary">
                            {PLATFORM_LABELS[review.platform] ??
                              review.platform}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {review.source_id}
                        </TableCell>
                        <TableCell className="text-sm">
                          {review.author_handle ?? "—"}
                        </TableCell>
                        <TableCell className="max-w-[280px]">
                          {review.text?.trim() ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-sm cursor-default">
                                  {truncateText(review.text)}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-sm whitespace-pre-wrap">
                                {review.text}
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="tabular-nums text-sm">
                          {review.native_rating != null
                            ? `${review.native_rating}${
                                review.native_rating_scale != null
                                  ? ` / ${review.native_rating_scale}`
                                  : ""
                              }`
                            : "—"}
                        </TableCell>
                        <TableCell className="tabular-nums text-sm">
                          {review.sentiment_score != null ? (
                            <span title={review.sentiment_method ?? undefined}>
                              {review.sentiment_score}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                          {formatDate(review.posted_at)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                          {formatDate(review.scraped_at)}
                        </TableCell>
                        <TableCell>
                          <a
                            href={review.url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center text-primary hover:underline"
                            title={review.url}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </TableCell>
                        <TableCell>
                          {review.raw_payload ? (
                            <details className="text-xs">
                              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                                Peek
                              </summary>
                              <JsonBlock
                                value={review.raw_payload}
                                className="mt-2 max-w-xs max-h-40 overflow-auto rounded border bg-muted/40 p-2 text-[10px]"
                              />
                            </details>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openSentiment(review)}
                          >
                            Set sentiment
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TooltipProvider>
          )}

          <Pagination
            currentPage={currentPage}
            totalItems={counts?.total ?? 0}
            itemsPerPage={limit}
            onPageChange={(page) =>
              setUrlState((prev) => ({
                ...prev,
                offset: (page - 1) * limit,
              }))
            }
          />
        </CardContent>
      </Card>

      <SentimentDialog
        open={sessionState.sentimentDialogOpen}
        review={selectedReview}
        onOpenChange={(open) =>
          setSessionState((prev) => ({
            ...prev,
            sentimentDialogOpen: open,
            selectedReviewId: open ? prev.selectedReviewId : null,
          }))
        }
      />

      <IngestReviewDialog
        open={sessionState.ingestDialogOpen}
        onOpenChange={(open) =>
          setSessionState((prev) => ({ ...prev, ingestDialogOpen: open }))
        }
        sources={sourceIds}
        sourcesLoading={sourcesQuery.isLoading}
      />
    </div>
  );
}
